import { forwardRef, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { Icon as IconComponent } from '../Icon';
import { Text } from '../Text';
import {
  StyledFlex,
  StyledFlexWrapper,
  StyledLabel,
  StyledRoot,
  StyledVericalFlex,
} from './SelectionGroup.style';

// ------ SelectionGroup Component ------ //
type SelectionGroupProps = {
  defaultValues?: Record<string, boolean>;
  type: 'checkbox' | 'radio';
  children: ({
    onChange,
    selectedOptions,
    type,
    isControlled,
  }: {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    selectedOptions: Record<string, boolean>;
    type: 'checkbox' | 'radio';
    isControlled: boolean;
  }) => React.ReactNode;
  onChange?: (value: string) => void;
  value?: Record<string, boolean>;
};

// ------ SelectionGroup Component ------ //
export const SelectionGroup = forwardRef<HTMLDivElement, SelectionGroupProps>(
  ({ children, defaultValues, type, value, onChange }, ref) => {
    const [selectedOptions, setSelectedOptions] = useState(value ? value : defaultValues ?? {});

    const isControlled = value !== undefined;

    useEffect(() => {
      if (isControlled && defaultValues !== undefined) {
        console.warn(
          'Warning: The `SelectionGroup.Root` component is being used as a controlled component (because `value` prop is provided) but also received `defaultValues` prop. ' +
            'In a controlled component, `defaultValues` will be ignored. Please remove the `defaultValues` prop for consistent behavior.'
        );
      }
    }, [isControlled, defaultValues]);

    useEffect(() => {
      if (value) {
        setSelectedOptions(value);
      }
    }, [value]);

    const handleControlledChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): null | void => {
        if (onChange) {
          onChange(event.target.value);
        }
      },
      [onChange]
    );

    const onChangeHandler = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isControlled) {
          handleControlledChange(event);

          return;
        }

        if (type === 'radio') {
          setSelectedOptions({
            [event.target.value]: true,
          });
        }

        if (type === 'checkbox') {
          setSelectedOptions((prev) => ({
            ...prev,
            [event.target.value]: event.target.checked,
          }));
        }
      },
      [handleControlledChange, isControlled, type]
    );

    return (
      <StyledRoot ref={ref}>
        {children({
          isControlled,
          onChange: onChangeHandler,
          selectedOptions,
          type,
        })}
      </StyledRoot>
    );
  }
) as React.ForwardRefExoticComponent<SelectionGroupProps> & {
  Option: typeof Option;
  DefaultOption: typeof DefaultOption;
};

SelectionGroup.displayName = 'SelectionGroup';

// ------ Option Component ------ //
type DefaultOptionProps = {
  title: string;
  subtitle: string;
  Icon: React.ReactNode;
  value: string;
  selectedOptions: Record<string, boolean>;
  disabled?: boolean;
};

export const DefaultOption = forwardRef<HTMLDivElement, DefaultOptionProps>(
  ({ Icon, subtitle, title, value, selectedOptions, disabled = false }, ref) => {
    const theme = useTheme();

    return (
      <StyledFlexWrapper ref={ref}>
        <StyledFlex>
          {Icon}
          <StyledVericalFlex>
            <Text
              size="BodyLargeHeavy"
              color={disabled ? theme.Color.Neutrals.Gray : theme.Color.Neutrals.Dark}
            >
              {title}
            </Text>
            <Text
              size="Body"
              color={disabled ? theme.Color.Neutrals.Gray : theme.Color.Neutrals.Gray}
            >
              {subtitle}
            </Text>
          </StyledVericalFlex>
        </StyledFlex>
        {selectedOptions[value] && (
          <IconComponent
            name="check"
            color={disabled ? theme.Color.Neutrals.Gray : theme.Color.Neutrals.Dark}
          />
        )}
      </StyledFlexWrapper>
    );
  }
);

DefaultOption.displayName = 'SelectionGroup.DefaultOption';

// ------ Card Component ------ //
type CardProps = {
  name: string;
  value: string;
  children?: ({ value, disabled }: { value: string; disabled?: boolean }) => React.ReactNode;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedOptions: Record<string, boolean>;
  type: 'radio' | 'checkbox';
  isControlled: boolean;
  disabled?: boolean;
};
const Option = forwardRef<HTMLLabelElement, CardProps>(
  (
    { isControlled, name, onChange, selectedOptions, type, value, children, disabled = false },
    ref
  ) => {
    const id = useId();
    const selectedValue = useMemo(
      () => Object.keys(selectedOptions).find((key) => selectedOptions[key]),
      [selectedOptions]
    );

    return (
      <StyledLabel disabled={disabled} ref={ref} htmlFor={id}>
        {children && children({ value, disabled })}
        <input
          disabled={disabled}
          onChange={onChange}
          type={type}
          data-checked={selectedValue === value}
          {...(isControlled
            ? {
                checked: selectedValue === value,
              }
            : {
                defaultChecked: selectedValue === value,
              })}
          name={name}
          id={id}
          value={value}
        />
      </StyledLabel>
    );
  }
);

Option.displayName = 'SelectionGroup.Card';

// ------ Assigning Components to SelectionGroup ------ //
SelectionGroup.Option = Option;
SelectionGroup.DefaultOption = DefaultOption;
